/*
 * AVALY cart controller.
 *
 * One owner for cart add/change/remove + drawer rendering. The theme still
 * provides the drawer shell and close behaviour, but this controller intercepts
 * purchase and cart-control events in capture phase so the older theme/custom
 * cart paths cannot double-submit or mutate the wrong Shopify line.
 */
(function () {
  'use strict';

  var CART_ROOT = '[data-quick-cart]';
  var CACHE_MS = 500;
  var DEBOUNCE_MS = 180;
  var REQUEST_TIMEOUT_MS = 7500;
  var CHANGE_SYNC_MS = 700;
  var inflightCart = null;
  var cartCache = null;
  var cartCacheAt = 0;
  var ops = {};
  var timers = {};
  var syncTimer = null;
  var changeQueue = Promise.resolve();
  var lineChangeSeq = {};
  var desiredLineQty = {};
  var rowRefs = {};
  var emittingCartUpdate = false;
  var addProtection = null;
  var renderingCart = false;
  var reclaimTimer = null;
  var cartObserver = null;

  function $(selector, root) {
    return (root || document).querySelector(selector);
  }

  function $all(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function root() {
    return $(CART_ROOT);
  }

  function cartSelector(selector) {
    return $(CART_ROOT + ' ' + selector);
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function money(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var value = Number(cents || 0);
    var parts = (value / 100).toFixed(2).split('.');
    var dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format.replace(/\{\{\s*\w+\s*\}\}/, dollars + '.' + parts[1]);
  }

  function parseMoney(text) {
    var matches = String(text || '').match(/\$[\d,]+(?:\.\d{2})?/g);
    if (!matches || !matches.length) return 0;
    var value = Number(matches[matches.length - 1].replace(/[$,]/g, ''));
    return Number.isFinite(value) ? Math.round(value * 100) : 0;
  }

  function sizedImage(src, size) {
    if (!src) return '';
    var clean = String(src).replace(/^http(s)?:/, '');
    var match = clean.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp)(\?[^#]*)?$/i);
    if (!match) return clean;
    var suffix = match[0];
    var base = clean.slice(0, -suffix.length)
      .replace(/_(?:pico|icon|thumb|small|compact|medium|large|grande|original|master|\d+x\d*|x\d+)$/i, '');
    return base + '_' + size + suffix;
  }

  function routes() {
    var themeRoutes = window.theme && window.theme.routes && window.theme.routes.cart;
    return {
      base: ((themeRoutes && themeRoutes.base) || '/cart') + '.js',
      add: ((themeRoutes && themeRoutes.add) || '/cart/add') + '.js',
      change: ((themeRoutes && themeRoutes.change) || '/cart/change') + '.js',
      update: ((themeRoutes && themeRoutes.update) || '/cart/update') + '.js'
    };
  }

  function getCart(force) {
    var now = Date.now();
    if (!force && cartCache && now - cartCacheAt < CACHE_MS) return Promise.resolve(cartCache);
    if (!force && inflightCart) return inflightCart;

    inflightCart = requestJson(routes().base, {
      cache: 'no-store',
      credentials: 'same-origin'
    })
      .then(function (cart) {
        inflightCart = null;
        return setCachedCart(cart);
      }, function (error) {
        inflightCart = null;
        throw error;
      });

    return inflightCart;
  }

  function setCachedCart(cart) {
    cartCache = sortCart(cart);
    cartCacheAt = Date.now();
    return cartCache;
  }

  function cloneCart(cart) {
    var source = cart || cartCache || {};
    return JSON.parse(JSON.stringify({
      items: source.items || [],
      item_count: source.item_count || 0,
      total_price: source.total_price || 0,
      original_total_price: source.original_total_price || 0,
      total_discount: source.total_discount || 0,
      cart_level_discount_applications: source.cart_level_discount_applications || []
    }));
  }

  function json(response) {
    return response.text().then(function (text) {
      var body = {};
      try {
        body = text ? JSON.parse(text) : {};
      } catch (parseError) {
        var htmlError = new Error('Cart request returned a non-JSON response');
        htmlError.status = response.status;
        htmlError.responseText = text ? text.slice(0, 240) : '';
        throw htmlError;
      }
      if (!response.ok || body.status === 422) {
        var error = new Error(body.description || body.message || 'Cart request failed');
        error.status = response.status;
        error.body = body;
        throw error;
      }
      return body;
    });
  }

  function requestJson(url, options, timeoutMs) {
    timeoutMs = timeoutMs || REQUEST_TIMEOUT_MS;
    options = options || {};

    return new Promise(function (resolve, reject) {
      var settled = false;
      var controller = window.AbortController ? new AbortController() : null;
      var requestOptions = {};
      Object.keys(options).forEach(function (key) { requestOptions[key] = options[key]; });
      if (controller) requestOptions.signal = controller.signal;

      var timeoutId = setTimeout(function () {
        if (settled) return;
        settled = true;
        if (controller) controller.abort();
        var timeoutError = new Error('Cart request timed out');
        timeoutError.code = 'cart_timeout';
        reject(timeoutError);
      }, timeoutMs);

      try {
        fetch(url, requestOptions)
          .then(json)
          .then(function (body) {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            resolve(body);
          }, function (error) {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            reject(error);
          });
      } catch (error) {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        reject(error);
      }
    });
  }

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function getOrder() {
    try {
      return JSON.parse(localStorage.getItem('neon_cart_order') || '[]');
    } catch (e) {
      return [];
    }
  }

  function setOrder(order) {
    try {
      localStorage.setItem('neon_cart_order', JSON.stringify(order));
    } catch (e) {}
  }

  function rememberAddedVariant(variantId) {
    var id = Number(variantId);
    if (!id) return;
    var existing = getOrder();
    setOrder([id].concat(existing.filter(function (item) { return item !== id; })));
  }

  function sortCart(cart) {
    if (!cart) return cart;
    var order = getOrder();
    var items = cart.items || [];
    cart.sorted = order.length ? items.slice().sort(function (a, b) {
      var ai = order.indexOf(a.variant_id);
      var bi = order.indexOf(b.variant_id);
      if (ai === -1) ai = 9999;
      if (bi === -1) bi = 9999;
      return ai - bi;
    }) : items;
    return cart;
  }

  function lineUnitPrice(line) {
    if (!line) return 0;
    if (typeof line.final_price === 'number') return line.final_price;
    if (typeof line.final_line_price === 'number' && line.quantity) return Math.round(line.final_line_price / line.quantity);
    if (typeof line.line_price === 'number' && line.quantity) return Math.round(line.line_price / line.quantity);
    if (typeof line.price === 'number') return line.price;
    return 0;
  }

  function lineOriginalUnit(line) {
    if (!line) return 0;
    if (typeof line.original_price === 'number') return line.original_price;
    if (typeof line.original_line_price === 'number' && line.quantity) return Math.round(line.original_line_price / line.quantity);
    return lineUnitPrice(line);
  }

  function lineOriginalTotal(line) {
    if (!line) return 0;
    if (typeof line.original_line_price === 'number') return line.original_line_price;
    if (typeof line.original_price === 'number') return line.original_price * Number(line.quantity || 0);
    if (typeof line.final_line_price === 'number') return line.final_line_price;
    return lineOriginalUnit(line) * Number(line.quantity || 0);
  }

  function saleTiers() {
    return window.AVALY_SALE_TIERS || [{ q: 1, pct: 10 }, { q: 2, pct: 20 }, { q: 3, pct: 30 }, { q: 4, pct: 40 }];
  }

  function saleTierFor(units) {
    var tiers = saleTiers();
    var pct = 0;
    tiers.forEach(function (tier) {
      if (units >= Number(tier.q || 0)) pct = Number(tier.pct || 0);
    });
    return pct;
  }

  function isSaleLine(line) {
    var ids = window.AVALY_SALE_PRODUCT_IDS || [];
    return window.AVALY_SALE_SITEWIDE === true || ids.indexOf(line.product_id) !== -1;
  }

  function saleSummary(cart) {
    var items = (cart && cart.items) || [];
    var original = 0;
    var saleOriginal = 0;
    var saleUnits = 0;

    items.forEach(function (line) {
      if (line && line.pending) return;
      var lineOriginal = lineOriginalTotal(line);
      original += lineOriginal;
      if (window.AVALY_SALE_ON && isSaleLine(line)) {
        saleOriginal += lineOriginal;
        saleUnits += Number(line.quantity || 0);
      }
    });

    var pct = window.AVALY_SALE_ON ? saleTierFor(saleUnits) : 0;
    var saleSaving = Math.round(saleOriginal * pct / 100);
    var finalTotal = window.AVALY_SALE_ON && saleUnits > 0
      ? Math.max(0, original - saleSaving)
      : Number((cart && cart.total_price) || 0);

    return {
      original: original,
      finalTotal: finalTotal,
      saleOriginal: saleOriginal,
      saleSaving: saleSaving,
      saleUnits: saleUnits,
      pct: pct
    };
  }

  function subtotalHtml(cart, summary) {
    summary = summary || saleSummary(cart);
    var original = summary.original;
    var finalTotal = summary.finalTotal;

    if (original > finalTotal) {
      return '<span class="quick-cart__subtotal-prices">' +
        '<s class="quick-cart__subtotal-was">' + money(original) + '</s>' +
        '<span class="quick-cart__subtotal-now">' + money(finalTotal) + '</span>' +
      '</span>';
    }

    return money(finalTotal);
  }

  function setLineQty(line, quantity) {
    var unit = lineUnitPrice(line);
    var originalUnit = lineOriginalUnit(line) || unit;
    line.quantity = quantity;
    line.final_line_price = unit * quantity;
    line.line_price = unit * quantity;
    line.original_line_price = originalUnit * quantity;
  }

  function optimisticLineFromQuickAdd(button, variantId, quantity) {
    if (!button || !button.closest) return null;
    var quickAdd = button.closest('.avaly-quick-add');
    var card = button.closest('.avaly-card');
    if (!card) return null;

    var titleEl = $('.avaly-card__title a, .avaly-card__title', card);
    var linkEl = $('.avaly-card__title a, .avaly-card__link', card);
    var imageEl = $('.avaly-card__img', card);
    var priceEl = $('.avaly-card__sale-price', card) || $('.avaly-card__price', card);
    var selectedSize = quickAdd && $('.avaly-quick-add__size.selected', quickAdd);
    var unit = parseMoney(priceEl && priceEl.textContent);
    var qty = Math.max(1, Number(quantity || 1));
    var title = titleEl ? titleEl.textContent.replace(/\s+/g, ' ').trim() : '';
    var url = linkEl ? linkEl.getAttribute('href') : '';
    var image = imageEl ? (imageEl.currentSrc || imageEl.src || imageEl.getAttribute('src')) : '';
    var imageAlt = imageEl ? imageEl.getAttribute('alt') : '';
    var variantTitle = selectedSize ? selectedSize.textContent.replace(/\s+/g, ' ').trim() : '';

    if (!title || !unit) return null;

    return {
      key: 'optimistic:' + variantId + ':' + Date.now(),
      id: Number(variantId),
      variant_id: Number(variantId),
      quantity: qty,
      product_title: title,
      url: url || '#',
      final_price: unit,
      original_price: unit,
      final_line_price: unit * qty,
      original_line_price: unit * qty,
      line_price: unit * qty,
      image: image,
      featured_image: image ? { url: image, alt: imageAlt || title } : null,
      variant_title: variantTitle,
      options_with_values: variantTitle ? [{ name: 'Size', value: variantTitle }] : [],
      properties: {},
      line_level_discount_allocations: [],
      optimistic: true
    };
  }

  function productDataFromForm(form) {
    var scope = form && form.closest ? form.closest('.product') : null;
    var sectionId = scope && scope.getAttribute('data-section-id');
    var jsonEl = sectionId ? $('#ProductJson-' + sectionId) : null;
    if (!jsonEl) jsonEl = $('script[id^="ProductJson-"]');
    if (!jsonEl) return null;

    try {
      return JSON.parse(jsonEl.textContent || '{}');
    } catch (error) {
      return null;
    }
  }

  function findVariant(product, variantId) {
    var variants = product && product.variants;
    if (!variants || !variants.length) return null;
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(variantId)) return variants[i];
    }
    return null;
  }

  function imageUrlFromValue(value) {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.src || value.url || value.featured_image || '';
  }

  function visibleProductImage(form, product, variant) {
    var scope = form && form.closest ? form.closest('.product') : null;
    var imageEl = scope && $('.product__media-item:not(.hidden) img, .product__media-item img', scope);
    if (imageEl) {
      return {
        url: imageEl.currentSrc || imageEl.src || imageEl.getAttribute('src') || '',
        alt: imageEl.getAttribute('alt') || ''
      };
    }

    var image = imageUrlFromValue(variant && variant.featured_image) ||
      imageUrlFromValue(product && product.featured_image) ||
      imageUrlFromValue(product && product.images && product.images[0]);

    return { url: image, alt: (product && product.title) || '' };
  }

  function optionName(product, index) {
    var option = product && product.options && product.options[index];
    if (!option) return 'Option ' + (index + 1);
    return typeof option === 'string' ? option : (option.name || ('Option ' + (index + 1)));
  }

  function propertiesFromFormData(formData) {
    var properties = {};
    if (!formData || !formData.forEach) return properties;
    formData.forEach(function (value, key) {
      var match = String(key).match(/^properties\[(.+)\]$/);
      if (!match || value == null || value === '') return;
      properties[match[1]] = value;
    });
    return properties;
  }

  function productScope(form) {
    return form && form.closest ? form.closest('.product') : null;
  }

  function productError(form) {
    return $('[data-quantity-error]', productScope(form) || document);
  }

  function showProductError(form, message) {
    var errorEl = productError(form);
    if (!errorEl) return;
    if (message) errorEl.textContent = message;
    errorEl.classList.remove('hidden');
  }

  function hideProductError(form) {
    var errorEl = productError(form);
    if (errorEl) errorEl.classList.add('hidden');
  }

  function inventoryData(form) {
    var jsonEl = $('[data-product-inventory-json]', productScope(form) || document);
    if (!jsonEl) return null;
    try {
      return JSON.parse(jsonEl.textContent || '{}').inventory || null;
    } catch (error) {
      return null;
    }
  }

  function currentCartQuantity(variantId, cart) {
    var items = ((cart || cartCache) && (cart || cartCache).items) || [];
    var quantity = 0;
    items.forEach(function (line) {
      if (line && (line.pending || line.optimistic)) return;
      if (String(line.variant_id || line.id) === String(variantId)) {
        quantity += Number(line.quantity || 0);
      }
    });
    return quantity;
  }

  function inventoryLimitError(form, variantId, requestedQty, cart) {
    var inventory = inventoryData(form);
    var variantInventory = inventory && inventory[variantId];
    if (!variantInventory) return '';
    if (variantInventory.inventory_management !== 'shopify') return '';
    if (variantInventory.inventory_policy !== 'deny') return '';

    var available = Number(variantInventory.inventory_quantity);
    if (!Number.isFinite(available)) return '';
    var cartQuantity = currentCartQuantity(variantId, cart);
    var requestedTotal = cartQuantity + Math.max(1, Number(requestedQty || 1));
    if (requestedTotal <= available) return '';
    if (available <= 0) return 'This size is sold out.';
    if (cartQuantity >= available) return 'You already have all available stock for this size in your cart.';
    return 'Only ' + available + ' available for this size.';
  }

  function optimisticLineFromProductForm(form, formData, variantId, quantity) {
    if (!form || !variantId) return null;
    var product = productDataFromForm(form);
    var variant = findVariant(product, variantId);
    var scope = form.closest ? form.closest('.product') : null;
    var titleEl = scope && $('.product__title', scope);
    var priceEl = scope && $('[data-price]', scope);
    var title = (product && product.title) || (titleEl ? titleEl.textContent.replace(/\s+/g, ' ').trim() : '');
    var unit = variant && typeof variant.price === 'number' ? variant.price : parseMoney(priceEl && priceEl.textContent);
    var qty = Math.max(1, Number(quantity || 1));
    var image = visibleProductImage(form, product, variant);
    var variantTitle = variant && variant.title && variant.title !== 'Default Title' ? variant.title : '';
    var options = variant && variant.options && variant.options.length
      ? variant.options.map(function (value, index) {
        return { name: optionName(product, index), value: value };
      })
      : [];
    var url = (product && product.url) || window.location.pathname;

    if (!title || !unit) return null;

    return {
      key: 'optimistic:' + variantId + ':' + Date.now(),
      id: Number(variantId),
      variant_id: Number(variantId),
      product_id: product && product.id,
      quantity: qty,
      product_title: title,
      url: url + (url.indexOf('?') === -1 ? '?variant=' : '&variant=') + encodeURIComponent(variantId),
      final_price: unit,
      original_price: unit,
      final_line_price: unit * qty,
      original_line_price: unit * qty,
      line_price: unit * qty,
      image: image.url,
      featured_image: image.url ? { url: image.url, alt: image.alt || title } : null,
      variant_title: variantTitle,
      options_with_values: options,
      properties: propertiesFromFormData(formData),
      line_level_discount_allocations: [],
      optimistic: true
    };
  }

  function optimisticCartChange(lineKey, delta, remove) {
    if (!cartCache) return null;
    var cart = cloneCart(cartCache);
    var line = findLine(cart, lineKey, null);
    if (!line || line.pending || line.optimistic) return null;

    var oldQty = Number(line.quantity || 0);
    var newQty = remove ? 0 : Math.max(0, oldQty + delta);
    var qtyDelta = newQty - oldQty;
    var originalUnit = lineOriginalUnit(line);
    var unit = window.AVALY_SALE_ON && isSaleLine(line) ? originalUnit : lineUnitPrice(line);

    cart.item_count = Math.max(0, Number(cart.item_count || 0) + qtyDelta);
    cart.total_price = Math.max(0, Number(cart.total_price || 0) + unit * qtyDelta);
    cart.original_total_price = Math.max(0, Number(cart.original_total_price || 0) + originalUnit * qtyDelta);

    if (newQty === 0) {
      cart.items = cart.items.filter(function (item) { return String(item.key) !== String(lineKey); });
    } else {
      setLineQty(line, newQty);
    }

    render(cart);
    return newQty;
  }

  function optimisticPendingAdd(quantity) {
    return null;
  }

  function optimisticCartAdd(line) {
    if (!line) return null;
    var cart = cloneCart(cartCache);
    var qty = Math.max(1, Number(line.quantity || 1));
    var unit = lineUnitPrice(line);
    var originalUnit = lineOriginalUnit(line) || unit;

    rememberAddedVariant(line.variant_id || line.id);
    cart.items.unshift(line);
    cart.item_count = Math.max(0, Number(cart.item_count || 0) + qty);
    cart.total_price = Math.max(0, Number(cart.total_price || 0) + unit * qty);
    cart.original_total_price = Math.max(0, Number(cart.original_total_price || 0) + originalUnit * qty);
    protectAddedCart(cart, line.variant_id || line.id);
    render(cart);
    document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: line, cart: cart, optimistic: true } }));
    return line.key;
  }

  function rollbackPending(pendingKey) {
    if (!pendingKey || !cartCache) return;
    render(removePending(cloneCart(cartCache), pendingKey));
  }

  function recoverCartAfterError(pendingKey) {
    addProtection = null;
    rollbackPending(pendingKey);
    getCart(true).then(function (cart) { render(cart); }).catch(function () {});
  }

  function flushPendingChangesForVariant(variantId) {
    var id = variantId ? String(variantId) : '';
    Object.keys(ops).forEach(function (lineKey) {
      var op = ops[lineKey];
      if (id && op && op.variantId && String(op.variantId) !== id) return;
      clearTimeout(timers[lineKey]);
      flushChange(lineKey);
    });
    return changeQueue.catch(function () {});
  }

  function hasLineOptions(formData) {
    var hasOptions = false;
    if (!formData || !formData.forEach) return false;
    formData.forEach(function (value, key) {
      if (hasOptions) return;
      if (key === 'id' || key === 'quantity' || key === 'form_type' || key === 'utf8') return;
      if (value == null || value === '') return;
      hasOptions = true;
    });
    return hasOptions;
  }

  function updateCartQuantity(variantId, quantity) {
    var updates = {};
    updates[String(variantId)] = Math.max(0, Number(quantity || 0));
    return requestJson(routes().update, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ updates: updates })
    });
  }

  function addVariantWithUpdate(variantId, requestedQty, currentCart) {
    var id = String(variantId);
    var targetQty = currentCartQuantity(id, currentCart) + Math.max(1, Number(requestedQty || 1));

    return updateCartQuantity(id, targetQty).then(function (cart) {
      var item = findLine(cart, null, id);
      if (!item || currentCartQuantity(id, cart) < targetQty) {
        var error = new Error('We could not update your cart. Please try again.');
        error.status = 422;
        throw error;
      }
      return { cart: cart, item: item, variantId: id, fromUpdate: true };
    });
  }

  function isAddRaceError(error) {
    if (!error || error.status !== 422) return false;
    return /already in your cart|maximum quantity|exceeds current stock|current stock/i.test(error.message || '');
  }

  function retryAddAfterPendingChange(error, variantId, sendAdd) {
    if (!isAddRaceError(error)) return Promise.reject(error);
    return getCart(true)
      .then(function (cart) {
        if (findLine(cart, null, variantId)) throw error;
        return wait(260);
      })
      .then(function () { return flushPendingChangesForVariant(variantId); })
      .then(function () { return getCart(true); })
      .then(sendAdd);
  }

  function syncCartSoon(delay) {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      getCart(true).then(function (cart) { renderFreshCart(cart); }).catch(function () {});
    }, delay || CHANGE_SYNC_MS);
  }

  function removePending(cart, pendingKey) {
    if (!pendingKey) return cart;
    var removed = null;
    cart.items = (cart.items || []).filter(function (line) {
      var match = String(line.key) === String(pendingKey);
      if (match) removed = line;
      return !match;
    });
    if (removed) {
      var qty = Math.max(0, Number(removed.quantity || 0));
      var unit = lineUnitPrice(removed);
      var originalUnit = lineOriginalUnit(removed) || unit;
      cart.item_count = Math.max(0, Number(cart.item_count || 0) - qty);
      cart.total_price = Math.max(0, Number(cart.total_price || 0) - unit * qty);
      cart.original_total_price = Math.max(0, Number(cart.original_total_price || 0) - originalUnit * qty);
    }
    return cart;
  }

  function mergeAddedItem(item, pendingKey, requestedQuantity) {
    var cart = removePending(cloneCart(cartCache), pendingKey);
    var qty = Math.max(1, Number(requestedQuantity || item.quantity || 1));
    var existing = findLine(cart, item.key, item.variant_id || item.id);
    var unit = lineUnitPrice(item);

    if (existing) {
      setLineQty(existing, Number(existing.quantity || 0) + qty);
    } else {
      item.quantity = qty;
      setLineQty(item, qty);
      cart.items.unshift(item);
    }

    cart.item_count = Math.max(0, Number(cart.item_count || 0) + qty);
    cart.total_price = Math.max(0, Number(cart.total_price || 0) + unit * qty);
    cart.original_total_price = Math.max(0, Number(cart.original_total_price || 0) + lineOriginalTotal(item));
    return sortCart(cart);
  }

  function findLine(cart, key, variantId) {
    var items = (cart && cart.items) || [];
    var i;
    if (key) {
      for (i = 0; i < items.length; i++) {
        if (String(items[i].key) === String(key)) return items[i];
      }
    }
    if (variantId) {
      for (i = 0; i < items.length; i++) {
        if (String(items[i].variant_id || items[i].id) === String(variantId)) return items[i];
      }
    }
    return null;
  }

  function lineQuantity(cart, key, variantId) {
    var line = findLine(cart, key, variantId);
    return line ? Number(line.quantity || 0) : 0;
  }

  function iconPlus() {
    return '<div class="icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" d="M12 5v14M5 12h14"/></svg></div>';
  }

  function iconMinus() {
    return '<div class="icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" d="M5 12h14"/></svg></div>';
  }

  function iconTrash() {
    return '<div class="icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></div>';
  }

  function imageHtml(line) {
    var src = line.featured_image ? line.featured_image.url : line.image;
    var alt = line.featured_image ? line.featured_image.alt : '';
    var sized = sizedImage(src, '240x');
    return sized ? '<img class="image__img" alt="' + escapeHtml(alt || line.product_title) + '" src="' + escapeHtml(sized) + '" />' : '';
  }

  function saleLineSaving(line, summary) {
    if (!summary || !summary.pct || !window.AVALY_SALE_ON || !isSaleLine(line)) return 0;
    return Math.round(lineOriginalTotal(line) * summary.pct / 100);
  }

  function saleLineFinalUnit(line, summary) {
    var originalUnit = lineOriginalUnit(line);
    if (!summary || !summary.pct || !window.AVALY_SALE_ON || !isSaleLine(line)) return lineUnitPrice(line);
    return Math.max(0, Math.round(originalUnit * (100 - summary.pct) / 100));
  }

  function priceHtml(line, summary) {
    var html = '';
    var originalUnit = lineOriginalUnit(line);
    var finalUnit = saleLineFinalUnit(line, summary);
    if (originalUnit > finalUnit) html += '<s class="qty">' + money(originalUnit) + '</s> ';
    html += money(finalUnit);
    if (line.quantity > 1) html += ' <span class="c-subdued">x ' + line.quantity + '</span>';
    return html;
  }

  function optionsHtml(line) {
    var html = '';
    if (line.options_with_values && line.options_with_values.length && line.variant_title) {
      line.options_with_values.forEach(function (option) {
        html += '<div>' + escapeHtml(option.name) + ': ' + escapeHtml(option.value) + '</div>';
      });
    }
    if (line.properties) {
      Object.keys(line.properties).forEach(function (key) {
        if (key.charAt(0) === '_' || line.properties[key] == null || line.properties[key] === '') return;
        html += '<div class="quick-cart__item-properties">' + escapeHtml(key) + ': ' + escapeHtml(line.properties[key]) + '</div>';
      });
    }
    return html;
  }

  function lineDiscountsHtml(line, summary) {
    var saleSaving = saleLineSaving(line, summary);
    if (saleSaving > 0) {
      return '<ul class="quick-cart__item-discounts fs-body-small fs-body-base-ns c-subdued">' +
        '<li>Birthday sale (-' + money(saleSaving) + ')</li>' +
      '</ul>';
    }

    var allocations = line.line_level_discount_allocations || [];
    if (!allocations.length) return '';
    return '<ul class="quick-cart__item-discounts fs-body-small fs-body-base-ns c-subdued">' +
      allocations.map(function (allocation) {
        var title = allocation.discount_application && allocation.discount_application.title;
        return '<li>' + escapeHtml(title || 'Discount') + ' (-' + money(allocation.amount) + ')</li>';
      }).join('') +
      '</ul>';
  }

  function rowHtml(line, summary) {
    var strings = (window.theme && window.theme.strings && window.theme.strings.quickCart) || {};
    if (line.pending) {
      return '' +
        '<div class="quick-cart__item ff-body fs-body-base is-updating" data-line-key="' + escapeHtml(line.key) + '">' +
          '<div class="quick-cart__item-left"><div class="quick-cart__image"></div></div>' +
          '<div class="quick-cart__item-middle">' +
            '<h4 class="fs-body-bold">' + escapeHtml(line.product_title) + '</h4>' +
            '<div class="c-subdued">Updating your cart...</div>' +
          '</div>' +
        '</div>';
    }
    var key = escapeHtml(line.key);
    var variantId = escapeHtml(line.variant_id || line.id);
    var disabledAttrs = line.optimistic ? ' disabled aria-disabled="true" tabindex="-1"' : '';
    var controlsHtml =
      '<div class="quick-cart__control">' +
        '<div class="quick-cart__control-top">' +
          '<button type="button" class="quick-cart__button quick-cart__button-increase" data-avaly-increase aria-label="' + escapeHtml(strings.addProductQuantity || 'Add product quantity') + '"' + disabledAttrs + '>' + iconPlus() + '</button>' +
          '<button type="button" class="quick-cart__button quick-cart__button-decrease" data-avaly-decrease aria-label="' + escapeHtml(strings.removeProductQuantity || 'Subtract product quantity') + '"' + disabledAttrs + '>' + iconMinus() + '</button>' +
          '<button type="button" class="quick-cart__button quick-cart__button-remove" data-avaly-remove aria-label="' + escapeHtml(strings.removeProduct || 'Remove') + '"' + disabledAttrs + '>' + iconTrash() + '</button>' +
        '</div>' +
      '</div>';

    return '' +
      '<div class="quick-cart__item ff-body fs-body-base' + (line.optimistic ? ' is-updating' : '') + '" data-line-key="' + key + '" data-id="' + variantId + '" data-quantity="' + line.quantity + '">' +
        '<div class="quick-cart__item-left">' +
          '<a href="' + escapeHtml(line.url) + '"><div class="quick-cart__image">' + imageHtml(line) + '</div></a>' +
        '</div>' +
        '<div class="quick-cart__item-middle">' +
          '<h4 class="fs-body-bold"><a href="' + escapeHtml(line.url) + '">' + escapeHtml(line.product_title) + '</a></h4>' +
          '<div>' + priceHtml(line, summary) + '</div>' +
          optionsHtml(line) +
          lineDiscountsHtml(line, summary) +
        '</div>' +
        controlsHtml +
      '</div>';
  }

  function cartDiscountsHtml(cart) {
    var apps = cart.cart_level_discount_applications || [];
    if (!apps.length) return '';
    return '<ul>' + apps.map(function (app) {
      return '<li>' + escapeHtml(app.title || 'Discount') + ' (-' + money(app.total_allocated_amount) + ')</li>';
    }).join('') + '</ul>';
  }

  function render(cart, options) {
    options = options || {};
    cart = setCachedCart(cart);

    var itemsEl = cartSelector('[data-items]');
    var emptyEl = cartSelector('[data-empty]');
    var footerEl = cartSelector('[data-footer]');
    var subtotalEl = cartSelector('[data-subtotal]');
    var discountsEl = cartSelector('[data-discounts]');
    var rows = cart.sorted || cart.items || [];
    var realRows = rows.filter(function (line) { return !(line && (line.pending || line.optimistic)); });
    var summary = saleSummary(cart);

    renderingCart = true;
    try {
      if (itemsEl) itemsEl.innerHTML = rows.map(function (line) { return rowHtml(line, summary); }).join('');
      if (subtotalEl) subtotalEl.innerHTML = subtotalHtml(cart, summary);
      if (discountsEl) {
        discountsEl.innerHTML = cartDiscountsHtml(cart);
        discountsEl.classList.toggle('visible', Boolean(cart.cart_level_discount_applications && cart.cart_level_discount_applications.length));
      }
      if (emptyEl) emptyEl.classList.toggle('visible', rows.length === 0);
      if (footerEl) footerEl.classList.toggle('visible', realRows.length > 0);
      $all('[data-js-cart-count]').forEach(function (el) {
        el.textContent = cart.item_count;
      });
    } finally {
      setTimeout(function () { renderingCart = false; }, 0);
    }

    document.dispatchEvent(new CustomEvent('quick-cart:updated', { detail: { cart: cart } }));
    if (options.emit === true) {
      emittingCartUpdate = true;
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
      emittingCartUpdate = false;
    }
  }

  function hasLegacyCartRows() {
    var itemsEl = cartSelector('[data-items]');
    if (!itemsEl) return false;
    return $all('.quick-cart__item', itemsEl).some(function (row) {
      return !row.hasAttribute('data-line-key') || !row.querySelector('[data-avaly-increase],[data-avaly-decrease],[data-avaly-remove]');
    });
  }

  function reclaimCartRows(force) {
    clearTimeout(reclaimTimer);
    reclaimTimer = setTimeout(function () {
      if (renderingCart) return;
      if (!hasLegacyCartRows()) return;
      if (cartCache && !force) {
        render(cartCache, { emit: false });
        return;
      }
      getCart(true).then(function (cart) { render(cart, { emit: false }); }).catch(function () {});
    }, 20);
  }

  function observeCartRows() {
    var itemsEl = cartSelector('[data-items]');
    if (!itemsEl || !window.MutationObserver || cartObserver) return;
    cartObserver = new MutationObserver(function () {
      if (!renderingCart) reclaimCartRows(false);
    });
    cartObserver.observe(itemsEl, { childList: true });
  }

  function protectAddedCart(cart, variantId) {
    addProtection = {
      until: Date.now() + REQUEST_TIMEOUT_MS + 1000,
      minCount: Number((cart && cart.item_count) || 0),
      variantId: variantId ? String(variantId) : ''
    };
  }

  function isProtectedStale(cart) {
    if (!addProtection) return false;
    if (Date.now() > addProtection.until) {
      addProtection = null;
      return false;
    }
    if (Number((cart && cart.item_count) || 0) < addProtection.minCount) return true;
    if (addProtection.variantId && !findLine(cart, null, addProtection.variantId)) return true;
    return false;
  }

  function renderFreshCart(cart) {
    if (isProtectedStale(cart)) return false;
    render(cart);
    return true;
  }

  function syncAfterAdd(expectedCart, variantId, attempt) {
    attempt = attempt || 0;
    getCart(true).then(function (cart) {
      if (renderFreshCart(cart)) return;
      if (attempt < 4) {
        setTimeout(function () {
          syncAfterAdd(expectedCart, variantId, attempt + 1);
        }, 220 + attempt * 180);
      }
    }).catch(function () {});
  }

  function openDrawer(cart) {
    var el = root();
    if (!el) return;
    var cartPanel = $('[data-cart]', el);
    el.classList.add('active');
    if (cartPanel) cartPanel.classList.add('visible');
    setTimeout(function () {
      el.classList.add('visible');
      document.dispatchEvent(new CustomEvent('quick-cart:open', { detail: { cart: cartCache || cart } }));
    }, 50);
  }

  function setButtonState(button, state) {
    if (!button) return;
    if (state === 'loading') {
      button.disabled = true;
      button.classList.add('loading');
      button.classList.remove('item-added');
      return;
    }
    if (state === 'added') {
      button.disabled = false;
      button.classList.remove('loading');
      button.classList.add('item-added');
      setTimeout(function () { button.classList.remove('item-added'); }, 1800);
      return;
    }
    button.disabled = false;
    button.classList.remove('loading', 'item-added');
  }

  function closeQuickAddModal(context) {
    var modal = context && context.closest && context.closest('[data-quick-add-modal]');
    if (!modal) return;
    var closeButton = $('[data-quick-add-modal-close]', modal);
    if (closeButton) {
      closeButton.click();
      return;
    }
    modal.classList.remove('is-visible', 'active');
    setTimeout(function () { modal.classList.remove('is-fixed'); }, 300);
  }

  function addForm(form, button) {
    setButtonState(button, 'loading');
    var formData = new FormData(form);
    var requestedQty = Math.max(1, Number(formData.get('quantity') || 1));
    var variantId = formData.get('id');
    hideProductError(form);
    var pendingKey = optimisticCartAdd(optimisticLineFromProductForm(form, formData, variantId, requestedQty)) || optimisticPendingAdd(requestedQty);
    var body = new URLSearchParams(formData).toString();
    var sendAdd = function (currentCart) {
      if (!hasLineOptions(formData)) {
        return addVariantWithUpdate(variantId, requestedQty, currentCart);
      }
      return requestJson(routes().add, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: body
      });
    };
    openDrawer(cartCache);
    return flushPendingChangesForVariant()
      .then(function () { return getCart(true); })
      .then(function (cart) {
        var inventoryError = inventoryLimitError(form, variantId, requestedQty, cart);
        if (inventoryError) {
          var error = new Error(inventoryError);
          error.status = 422;
          throw error;
        }
        return sendAdd(cart);
      })
      .catch(function (error) { return retryAddAfterPendingChange(error, variantId, sendAdd); })
      .then(function (result) {
        var item = result && result.item ? result.item : result;
        rememberAddedVariant(item.variant_id);
        var optimisticCart = result && result.cart ? sortCart(result.cart) : mergeAddedItem(item, pendingKey, requestedQty);
        render(optimisticCart);
        protectAddedCart(optimisticCart, item.variant_id);
        closeQuickAddModal(form);
        setButtonState(button, 'added');
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: item, cart: optimisticCart, skipConfetti: Boolean(pendingKey) } }));
        syncAfterAdd(optimisticCart, item.variant_id, 0);
        return optimisticCart;
      })
      .catch(function (error) {
        setButtonState(button, 'idle');
        recoverCartAfterError(pendingKey);
        showProductError(form, error && error.status === 422 ? error.message : 'We could not update your cart. Please try again.');
        document.dispatchEvent(new CustomEvent('cart:error', { detail: { error: error, errorMessage: error.message } }));
        throw error;
      });
  }

  function addVariant(variantId, quantity, button, optimisticLine) {
    setButtonState(button, 'loading');
    var requestedQty = Math.max(1, Number(quantity || 1));
    var pendingKey = optimisticCartAdd(optimisticLine) || optimisticPendingAdd(requestedQty);
    var sendAdd = function (currentCart) {
      return addVariantWithUpdate(variantId, requestedQty, currentCart);
    };
    openDrawer(cartCache);
    return flushPendingChangesForVariant()
      .then(function () { return getCart(true); })
      .then(sendAdd)
      .catch(function (error) { return retryAddAfterPendingChange(error, variantId, sendAdd); })
      .then(function (result) {
        var item = result && result.item ? result.item : result;
        rememberAddedVariant(item.variant_id || variantId);
        var optimisticCart = result && result.cart ? sortCart(result.cart) : mergeAddedItem(item, pendingKey, requestedQty);
        render(optimisticCart);
        protectAddedCart(optimisticCart, item.variant_id || variantId);
        setButtonState(button, 'added');
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: item, cart: optimisticCart, skipConfetti: Boolean(optimisticLine) } }));
        syncAfterAdd(optimisticCart, item.variant_id || variantId, 0);
        return optimisticCart;
      })
      .catch(function (error) {
        setButtonState(button, 'idle');
        recoverCartAfterError(pendingKey);
        document.dispatchEvent(new CustomEvent('cart:error', { detail: { error: error, errorMessage: error.message } }));
        throw error;
      });
  }

  function scheduleChange(lineKey) {
    clearTimeout(timers[lineKey]);
    timers[lineKey] = setTimeout(function () { flushChange(lineKey); }, DEBOUNCE_MS);
  }

  function flushChange(lineKey) {
    var op = ops[lineKey];
    if (!op) return;
    delete ops[lineKey];

    if (typeof op.optimisticQty !== 'number') {
      return getCart(true).then(function (cart) {
        var line = findLine(cart, lineKey, op.variantId);
        if (!line) {
          renderFreshCart(cart);
          return null;
        }
        var qty = op.remove ? 0 : Math.max(0, Number(line.quantity || 0) + op.delta);
        return changeLine(line.key, qty, op.variantId);
      }).catch(function () {
        getCart(true).then(function (cart) { renderFreshCart(cart); }).catch(function () {});
      });
    }

    return changeLine(lineKey, op.optimisticQty, op.variantId);
  }

  function changeLine(lineKey, quantity, variantId, attempt) {
    attempt = attempt || 0;
    var seq = (lineChangeSeq[lineKey] || 0) + 1;
    lineChangeSeq[lineKey] = seq;
    desiredLineQty[lineKey] = quantity;

    changeQueue = changeQueue.catch(function () {}).then(function () {
      return requestJson(routes().change, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: lineKey, quantity: quantity })
      });
    }).then(function (cart2) {
        if (seq !== lineChangeSeq[lineKey]) return cart2;
        if (desiredLineQty[lineKey] !== quantity) return cart2;

        if (lineQuantity(cart2, lineKey, variantId) !== quantity && attempt < 2) {
          setTimeout(function () {
            changeLine(lineKey, quantity, variantId, attempt + 1);
          }, 80);
          return cart2;
        }

        delete desiredLineQty[lineKey];
        if (renderFreshCart(cart2)) {
          syncCartSoon();
        } else {
          syncCartSoon(1200);
        }
        return cart2;
      })
      .catch(function () {
        if (seq === lineChangeSeq[lineKey]) {
          delete desiredLineQty[lineKey];
          getCart(true).then(function (cart) { renderFreshCart(cart); }).catch(function () {});
          syncCartSoon(1200);
        }
      });

    return changeQueue;
  }

  function handleCartControl(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var button = target.closest('button[data-avaly-increase],button[data-avaly-decrease],button[data-avaly-remove],button[data-increase],button[data-decrease],button[data-remove]');
    if (!button || !button.closest(CART_ROOT)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    var row = button.closest('.quick-cart__item');
    var lineKey = row && row.getAttribute('data-line-key');
    var variantId = row && row.getAttribute('data-id');

    if (!lineKey) {
      variantId = button.getAttribute('data-increase') || button.getAttribute('data-decrease') || button.getAttribute('data-remove') || variantId;
      var line = findLine(cartCache, null, variantId);
      lineKey = line && line.key;
    }
    if (!lineKey) return;

    addProtection = null;
    rowRefs[lineKey] = row;
    var op = ops[lineKey] || { delta: 0, remove: false, variantId: variantId };
    var clickDelta = 0;
    if (button.hasAttribute('data-avaly-remove') || button.hasAttribute('data-remove')) {
      op.remove = true;
    } else if (button.hasAttribute('data-avaly-increase') || button.hasAttribute('data-increase')) {
      clickDelta = 1;
      op.delta += 1;
    } else {
      clickDelta = -1;
      op.delta -= 1;
    }
    var optimisticQty = optimisticCartChange(lineKey, clickDelta, op.remove);
    if (optimisticQty === null && row) {
      optimisticQty = op.remove ? 0 : Math.max(0, Number(row.getAttribute('data-quantity') || 0) + op.delta);
    }
    op.optimisticQty = optimisticQty;
    ops[lineKey] = op;
    scheduleChange(lineKey);
  }

  function handleProductSubmit(event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[data-product-form]')) return;
    var button = $('[data-add-to-cart]', form);
    if (button && button.disabled) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    addForm(form, button).catch(function () {});
  }

  function handleProductAddClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var button = target.closest('button[data-add-to-cart]');
    if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    var form = button.closest('form[data-product-form]');
    if (!form) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    addForm(form, button).catch(function () {});
  }

  function handleQuickAddSize(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var sizeButton = target.closest('.avaly-quick-add__size');
    if (!sizeButton || sizeButton.disabled || sizeButton.classList.contains('avaly-quick-add__size--unavailable')) return;

    var quickAdd = sizeButton.closest('.avaly-quick-add');
    if (!quickAdd) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    $all('.avaly-quick-add__size', quickAdd).forEach(function (button) {
      button.classList.remove('selected');
    });
    sizeButton.classList.add('selected');

    var addButton = $('.avaly-quick-add__btn', quickAdd);
    if (addButton) {
      addButton.dataset.variantId = sizeButton.dataset.variantId;
      addButton.disabled = false;
      addButton.textContent = 'Add to Cart';
    }
  }

  function handleQuickAdd(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var avalyButton = target.closest('.avaly-quick-add__btn');
    if (avalyButton) {
      var variantId = avalyButton.dataset.variantId;
      if (!variantId || avalyButton.disabled) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      var originalText = avalyButton.textContent;
      avalyButton.disabled = true;
      avalyButton.textContent = 'Adding...';
      avalyButton.classList.add('avaly-quick-add__btn--adding');
      addVariant(variantId, 1, null, optimisticLineFromQuickAdd(avalyButton, variantId, 1)).then(function () {
        avalyButton.textContent = 'Added!';
        avalyButton.classList.remove('avaly-quick-add__btn--adding');
        avalyButton.classList.add('avaly-quick-add__btn--added');
        setTimeout(function () {
          avalyButton.disabled = false;
          avalyButton.classList.remove('avaly-quick-add__btn--added');
          avalyButton.textContent = originalText === 'Adding...' ? 'Add to Cart' : originalText;
        }, 1600);
      }).catch(function () {
        avalyButton.disabled = false;
        avalyButton.classList.remove('avaly-quick-add__btn--adding');
        avalyButton.textContent = 'Error - Try Again';
        setTimeout(function () { avalyButton.textContent = originalText || 'Add to Cart'; }, 1800);
      });
      return;
    }

    var themeButton = target.closest('button[data-quick-add][data-product-id]:not([data-open-modal])');
    if (!themeButton) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    var quantityInput = $('[data-quantity-input]', themeButton.parentNode);
    addVariant(themeButton.dataset.productId, quantityInput ? quantityInput.value : 1, themeButton).catch(function () {});
  }

  document.addEventListener('submit', handleProductSubmit, true);
  document.addEventListener('click', handleProductAddClick, true);
  document.addEventListener('click', handleCartControl, true);
  document.addEventListener('click', handleQuickAddSize, true);
  document.addEventListener('click', handleQuickAdd, true);

  document.addEventListener('cart:updated', function (event) {
    if (emittingCartUpdate) return;
    if (event && event.detail && event.detail.cart && !isProtectedStale(event.detail.cart)) {
      render(event.detail.cart, { emit: false });
    }
  });

  function initCart() {
    observeCartRows();
    getCart(false).then(function (cart) {
      render(cart, { emit: false });
      setTimeout(function () { if (cartCache) render(cartCache, { emit: false }); }, 450);
      setTimeout(function () { if (cartCache) render(cartCache, { emit: false }); }, 1400);
    }).catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCart);
  } else {
    initCart();
  }

  /* ------------------------------------------------------------------
   * CHECKOUT GUARD (A7) - added 10 Sep 2026.
   *
   * THE BUG THIS FIXES
   * Quantity controls in the drawer update the UI optimistically and then wait
   * DEBOUNCE_MS (180ms) before sending /cart/change.js, and the request itself
   * can then sit in flight for up to REQUEST_TIMEOUT_MS (7.5s) on poor mobile
   * data. The checkout control is a plain <a href="/checkout">, so a customer
   * who nudged a quantity and tapped Checkout straight after navigated away
   * BEFORE Shopify's cart had the change. The drawer said 3, the checkout said
   * 2 - or a removed item was still in the order.
   *
   * THE FIX
   * Intercept checkout activation in capture phase, flush every debounced
   * change, wait for the change queue to settle, then re-trigger the control.
   * When nothing is pending this resolves on the next microtask, so a normal
   * checkout click is not measurably slower.
   *
   * It is deliberately best-effort: if the flush has not settled within
   * CHECKOUT_FLUSH_TIMEOUT_MS we go to checkout anyway. Sending someone to a
   * possibly-stale checkout is bad; trapping them behind a spinner is worse.
   *
   * If you add another way to reach checkout, add its selector to
   * CHECKOUT_SELECTOR or it will bypass this guard silently.
   * ------------------------------------------------------------------ */
  var CHECKOUT_FLUSH_TIMEOUT_MS = 2500;
  var CHECKOUT_SELECTOR = [
    '[data-buy-now-button]',
    'a[href="/checkout"]',
    'a[href^="/checkout?"]',
    'a[href*="/checkout"]',
    'button[name="checkout"]',
    'input[name="checkout"]'
  ].join(',');
  var checkoutReleasing = false;
  var checkoutBusy = false;

  function flushAllPendingChanges() {
    var pending = [];
    Object.keys(ops).forEach(function (lineKey) {
      clearTimeout(timers[lineKey]);
      var result;
      try {
        result = flushChange(lineKey);
      } catch (error) {
        result = null;
      }
      if (result && typeof result.then === 'function') {
        pending.push(result.catch(function () {}));
      }
    });

    return Promise.all(pending)
      .then(function () { return changeQueue.catch(function () {}); })
      .then(function () { return changeQueue.catch(function () {}); })
      .catch(function () {});
  }

  function settleOrTimeout(promise, ms) {
    return new Promise(function (resolve) {
      var settled = false;
      var timer = setTimeout(function () {
        if (settled) return;
        settled = true;
        resolve('timeout');
      }, ms);

      function finish() {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve('settled');
      }

      promise.then(finish, finish);
    });
  }

  function setCheckoutBusy(control, busy) {
    checkoutBusy = busy;
    if (!control) return;
    if (busy) {
      control.setAttribute('aria-busy', 'true');
      control.setAttribute('data-avaly-checkout-waiting', '');
    } else {
      control.removeAttribute('aria-busy');
      control.removeAttribute('data-avaly-checkout-waiting');
    }
  }

  function handleCheckoutClick(event) {
    if (checkoutReleasing) return;

    var target = event.target;
    if (!target || !target.closest) return;

    var control = target.closest(CHECKOUT_SELECTOR);
    if (!control) return;

    // Let the browser handle modified clicks (new tab, download, middle click).
    if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    if (checkoutBusy) {
      // A flush is already running for an earlier tap. Swallow the repeat so we
      // do not start a second one or double-submit the cart form.
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    setCheckoutBusy(control, true);

    settleOrTimeout(flushAllPendingChanges(), CHECKOUT_FLUSH_TIMEOUT_MS).then(function () {
      setCheckoutBusy(control, false);
      checkoutReleasing = true;
      try {
        control.click();
      } catch (error) {
        var href = control.getAttribute && control.getAttribute('href');
        if (href) window.location.href = href;
      }
      // Navigation is starting; if it is cancelled for any reason, allow the
      // guard to run again rather than leaving checkout permanently unguarded.
      setTimeout(function () { checkoutReleasing = false; }, 1500);
    });
  }

  document.addEventListener('click', handleCheckoutClick, true);

  window.AvalyCart = {
    addVariant: addVariant,
    getCart: getCart,
    open: openDrawer,
    render: render
  };
})();
